import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Image, FileText, Upload, User, Images, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPrivateFiles = () => {
  const [files, setFiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [selectedClient, setSelectedClient] = useState('all');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [form, setForm] = useState({
    client_id: '',
    title: '',
    description: '',
    file_type: 'gallery',
    images: [],
    file_url: '',
    thumbnail: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [filesRes, clientsRes] = await Promise.all([
        adminFetch(`${API}/admin/private-files`),
        adminFetch(`${API}/admin/clients`)
      ]);
      if (filesRes.ok) setFiles(await filesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (file = null) => {
    if (file) {
      setEditing(file);
      setForm({
        client_id: file.client_id,
        title: file.title,
        description: file.description || '',
        file_type: file.file_type,
        images: file.images || [],
        file_url: file.file_url || '',
        thumbnail: file.thumbnail || ''
      });
    } else {
      setEditing(null);
      setForm({
        client_id: selectedClient !== 'all' ? selectedClient : '',
        title: '',
        description: '',
        file_type: 'gallery',
        images: [],
        file_url: '',
        thumbnail: ''
      });
    }
    setShowModal(true);
  };

  const canUploadGallery = !!form.client_id && !!form.title.trim();

  const handleImageUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    );
    if (uploadedFiles.length === 0) return;
    if (!canUploadGallery) {
      toast.error('Elige el cliente y escribe el título de la galería antes de subir fotos');
      e.target.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: uploadedFiles.length });
    const newImages = [...form.images];
    const clientName = clients.find((c) => c.client_id === form.client_id)?.name || form.client_id;

    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'galerias-privadas');
      formData.append('subpath', `${clientName}/${form.title.trim()}`);
      formData.append('use_original_name', 'true');

      try {
        const response = await adminFetch(`${API}/admin/upload`, {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        if (response.ok) {
          newImages.push(data.url);
          if (!form.thumbnail && newImages.length === 1) {
            setForm(prev => ({ ...prev, thumbnail: data.url }));
          }
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
      setUploadProgress({ current: i + 1, total: uploadedFiles.length });
    }

    setForm(prev => ({ ...prev, images: newImages }));
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    if (newImages.length > form.images.length) {
      toast.success(`${newImages.length - form.images.length} imágenes subidas`);
    }
    e.target.value = '';
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await adminFetch(`${API}/admin/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        setForm(prev => ({ ...prev, file_url: data.url }));
        toast.success('Archivo subido');
      }
    } catch (error) {
      toast.error('Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      thumbnail: prev.thumbnail === prev.images[index] ? (prev.images[0] || '') : prev.thumbnail
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.title) {
      toast.error('Cliente y título son obligatorios');
      return;
    }
    if (form.file_type === 'gallery' && form.images.length === 0) {
      toast.error('Añade al menos una imagen a la galería');
      return;
    }
    if (form.file_type === 'document' && !form.file_url) {
      toast.error('Sube un archivo');
      return;
    }

    try {
      const url = editing 
        ? `${API}/admin/private-files/${editing.file_id}` 
        : `${API}/admin/private-files`;
      
      const response = await adminFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!response.ok) throw new Error('Error');
      const result = await response.json().catch(() => ({}));
      toast.success(editing ? 'Archivo actualizado' : 'Archivo creado');
      if (result.email?.sent) {
        toast.success(`Email de aviso enviado a ${result.email.to}`);
      } else if (result.email) {
        toast.error(`No se pudo enviar el email al cliente: ${result.email.reason}`, { duration: 8000 });
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este archivo?')) return;
    try {
      await adminFetch(`${API}/admin/private-files/${id}`, { 
        method: 'DELETE' 
      });
      toast.success('Archivo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getClientName = (clientId) => clients.find(c => c.client_id === clientId)?.name || 'Cliente desconocido';

  const filteredFiles = selectedClient === 'all' 
    ? files 
    : files.filter(f => f.client_id === selectedClient);

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Archivos Privados</h1>
            <p className="text-muted-foreground">Galerías y documentos exclusivos para cada cliente</p>
          </div>
          <Button onClick={() => openModal()} className="gap-2">
            <Plus className="w-4 h-4" /> Nuevo Archivo
          </Button>
        </div>

        {/* Client filter */}
        <div className="mb-6">
          <label className="text-xs text-muted-foreground block mb-2">Filtrar por cliente</label>
          <select 
            value={selectedClient} 
            onChange={e => setSelectedClient(e.target.value)}
            className="h-10 px-3 border border-input bg-background rounded-md min-w-[200px]"
          >
            <option value="all">Todos los clientes ({files.length})</option>
            {clients.map(client => {
              const count = files.filter(f => f.client_id === client.client_id).length;
              return (
                <option key={client.client_id} value={client.client_id}>
                  {client.name} ({count})
                </option>
              );
            })}
          </select>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-48 bg-muted animate-pulse" />)}
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border">
            <Images className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay archivos privados</p>
            <p className="text-sm text-muted-foreground mt-2">Asigna galerías de fotos a tus clientes</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFiles.map((file) => (
              <div key={file.file_id} className="bg-card border border-border overflow-hidden group">
                {/* Thumbnail */}
                <div className="aspect-video bg-muted relative">
                  {file.file_type === 'gallery' && file.thumbnail ? (
                    <img src={file.thumbnail} alt={file.title} className="w-full h-full object-cover" />
                  ) : file.file_type === 'gallery' && file.images?.[0] ? (
                    <img src={file.images[0]} alt={file.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {file.file_type === 'gallery' ? <Image className="w-12 h-12 text-muted-foreground" /> : <FileText className="w-12 h-12 text-muted-foreground" />}
                    </div>
                  )}
                  {file.file_type === 'gallery' && file.images?.length > 0 && (
                    <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1">
                      {file.images.length} fotos
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {file.file_type === 'gallery' && file.images?.length > 0 && (
                      <button onClick={() => { setPreviewFile(file); setPreviewIndex(0); }} className="p-2 bg-white/20 hover:bg-white/30 rounded" data-testid={`preview-${file.file_id}`}>
                        <Eye className="w-4 h-4 text-white" />
                      </button>
                    )}
                    <button onClick={() => openModal(file)} className="p-2 bg-white/20 hover:bg-white/30 rounded" data-testid={`edit-file-${file.file_id}`}>
                      <Pencil className="w-4 h-4 text-white" />
                    </button>
                    <button onClick={() => handleDelete(file.file_id)} className="p-2 bg-white/20 hover:bg-white/30 rounded" data-testid={`delete-file-${file.file_id}`}>
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
                {/* Info */}
                <div className="p-4">
                  <h3 className="font-medium mb-1">{file.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-3 h-3" />
                    <span>{getClientName(file.client_id)}</span>
                  </div>
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 ${file.file_type === 'gallery' ? 'bg-blue-500/10 text-blue-600' : 'bg-red-500/10 text-red-600'}`}>
                    {file.file_type === 'gallery' ? 'Galería' : 'Documento'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Gallery Preview Modal */}
      <AnimatePresence>
        {previewFile && (
          <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="gallery-preview-modal">
            {/* Header */}
            <div className="flex items-center justify-between p-4 text-white">
              <div>
                <h2 className="font-serif text-xl">{previewFile.title}</h2>
                <p className="text-sm text-white/60">
                  {getClientName(previewFile.client_id)} · {previewFile.images?.length} fotos · Vista previa del cliente
                </p>
              </div>
              <button onClick={() => setPreviewFile(null)} className="p-2 hover:bg-white/10 rounded">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Main Image */}
            <div className="flex-1 flex items-center justify-center relative px-16 pb-4">
              <button
                onClick={() => setPreviewIndex(i => Math.max(0, i - 1))}
                disabled={previewIndex === 0}
                className="absolute left-4 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30 text-white"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <motion.img
                key={previewIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                src={previewFile.images?.[previewIndex]}
                alt={`Foto ${previewIndex + 1}`}
                className="max-h-[60vh] max-w-full object-contain select-none"
                onContextMenu={e => e.preventDefault()}
                draggable={false}
              />

              <button
                onClick={() => setPreviewIndex(i => Math.min((previewFile.images?.length || 1) - 1, i + 1))}
                disabled={previewIndex >= (previewFile.images?.length || 1) - 1}
                className="absolute right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30 text-white"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
                {previewIndex + 1} / {previewFile.images?.length}
              </div>
            </div>

            {/* Thumbnails strip */}
            <div className="p-4 overflow-x-auto">
              <div className="flex gap-2 justify-center">
                {previewFile.images?.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPreviewIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 overflow-hidden border-2 transition-all ${
                      idx === previewIndex ? 'border-white scale-105' : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal */}
      {showModal && (
        <DraggableModal onClose={() => setShowModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[32rem] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editing ? 'Editar Archivo' : 'Nuevo Archivo Privado'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Cliente *</label>
                <select 
                  value={form.client_id} 
                  onChange={e => setForm({...form, client_id: e.target.value})}
                  data-testid="private-file-client-select"
                  className="w-full h-10 px-3 border border-input bg-background rounded-md"
                >
                  <option value="">Seleccionar cliente...</option>
                  {clients.map(client => (
                    <option key={client.client_id} value={client.client_id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Tipo</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({...form, file_type: 'gallery', file_url: ''})}
                    className={`flex-1 py-2 px-4 text-sm border ${form.file_type === 'gallery' ? 'bg-foreground text-background border-foreground' : 'border-border'}`}
                  >
                    <Image className="w-4 h-4 mx-auto mb-1" />
                    Galería
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({...form, file_type: 'document', images: []})}
                    className={`flex-1 py-2 px-4 text-sm border ${form.file_type === 'document' ? 'bg-foreground text-background border-foreground' : 'border-border'}`}
                  >
                    <FileText className="w-4 h-4 mx-auto mb-1" />
                    Documento
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Título *</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Ej: Boda María y Juan - Junio 2024" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Descripción</label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} />
              </div>

              {/* Gallery upload */}
              {form.file_type === 'gallery' && (
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Imágenes *</label>
                  {form.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {form.images.map((img, idx) => (
                        <div key={idx} className="relative aspect-square">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                          {form.thumbnail === img && (
                            <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 text-white px-1">Portada</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={`border border-dashed border-border p-4 text-center ${canUploadGallery ? '' : 'opacity-60'}`}>
                    <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" id="gallery-upload" disabled={!canUploadGallery || uploading} data-testid="gallery-upload-input" />
                    <label htmlFor="gallery-upload" className={canUploadGallery ? 'cursor-pointer' : 'cursor-not-allowed'}>
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground" data-testid="gallery-upload-hint">
                        {uploading
                          ? 'Subiendo...'
                          : canUploadGallery
                            ? 'Haz clic para añadir imágenes (se guardan con su nombre original)'
                            : 'Elige el cliente y escribe el título de la galería antes de subir fotos'}
                      </p>
                    </label>
                  </div>
                  {uploading && uploadProgress.total > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Subiendo imagen {uploadProgress.current} de {uploadProgress.total}</span>
                        <span>{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-foreground h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Document upload */}
              {form.file_type === 'document' && (
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Archivo *</label>
                  {form.file_url ? (
                    <div className="flex items-center gap-2 p-3 bg-muted border border-border">
                      <FileText className="w-5 h-5 text-red-500" />
                      <span className="text-sm flex-1 truncate">{form.file_url.split('/').pop()}</span>
                      <button type="button" onClick={() => setForm({...form, file_url: ''})} className="text-destructive text-sm">Cambiar</button>
                    </div>
                  ) : (
                    <div className="border border-dashed border-border p-4 text-center">
                      <input type="file" onChange={handleDocUpload} className="hidden" id="doc-upload-private" />
                      <label htmlFor="doc-upload-private" className="cursor-pointer">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {uploading ? 'Subiendo...' : 'Haz clic para subir archivo'}
                        </p>
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" disabled={uploading} data-testid="private-file-save-btn">Guardar</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminPrivateFiles;
